import type { FC } from 'react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';
import type { StyledProps } from '@compiled/react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { OptionalEmojiDescription } from '@atlaskit/emoji/types';

import { useAIEmojiSuggestion } from './AIEmojiSuggestionStore.ts';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { createEditorContentUnifiedQueryVariables } from '@confluence/editor-content-unified-query/entry-points/createEditorContentUnifiedQueryVariables.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { withPageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container/entry-points/withPageTreeStateUpdater.ts';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useUpdateContentProperty } from '@confluence/update-content-property-utils/entry-points/useUpdateContentProperty.ts';
import { isExperimentEnabled, expValEquals } from '@confluence/feature-experiments';

import { trackDraftCreatedWithEmoji } from '../analytics.ts';
import { EmojiPickerLoader } from '../EmojiPickerLoader.ts';
import { getEmojiFromEmojiId, onRemoveEmoji, onSelectEmoji } from '../emojiMutationHelpers.ts';
import {
	getHasEmoji,
	getEmojiId,
	getHasLiveDocsDefaultEmoji,
	getEmojiProvider,
	DefaultTitleEmojiValues as DefaultTitleEmojiTransformerValues,
} from '../transformer.ts';
import { useEmojiStatusFieldName } from '../useEmojiStatusFieldName.ts';

import type {
	EmojiTitleButtonComponentProps,
	UpdateDefaultEmojiArgs,
} from './__types__/EmojiTitleButton.ts';
import { EmojiTitlePopover } from './EmojiTitlePopover.tsx';
import { EmojiTitleStateContainer } from './EmojiTitleStateContainer.ts';
import { useEmojiTitleFragment } from './useEmojiTitleFragment.ts';
import EmojiTitleQueryArtifact, {
	type EmojiTitleQuery$data,
	type EmojiTitleQuery$variables,
} from './__generated__/EmojiTitleQuery.graphql.ts';
import {
	ConfluenceGraphQLDefaultTitleEmoji as DefaultTitleEmojiValues,
	type EmojiTitleButtonUpdateDefaultTitleEmojiMutation,
	type EmojiTitleButtonUpdateDefaultTitleEmojiMutation_confluence_updateDefaultTitleEmoji_ConfluenceUpdateDefaultTitleEmojiPayload_errors_MutationError_extensions as MutationErrorExtensions,
	type ConfluenceGraphQLDefaultTitleEmoji,
} from './__types__/EmojiTitleButtonUpdateDefaultTitleEmojiMutation.ts';
import { graphql } from 'react-relay';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import {
	mutationErrorExtensionsAggCompat,
	type MutationErrorExtensionsAgg,
	// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
} from '@confluence/apollo-relay-interop/entry-points/mutationErrorExtensionsAggCompat.ts';
import type {
	EmojiTitleButtonUpdateDefaultTitleEmojiMutation$data,
	EmojiTitleButtonUpdateDefaultTitleEmojiMutation$variables,
} from './__generated__/EmojiTitleButtonUpdateDefaultTitleEmojiMutation.graphql.ts';

const EmojiTitleButtonUpdateDefaultTitleEmojiMutation = graphql`
	mutation EmojiTitleButtonUpdateDefaultTitleEmojiMutation(
		$cloudId: ID!
		$input: ConfluenceUpdateDefaultTitleEmojiInput!
	) {
		confluence_updateDefaultTitleEmoji(cloudId: $cloudId, input: $input) {
			contentId
			success
			errors {
				message
				extensions {
					errorType
					statusCode
				}
			}
		}
	}
`;

const processDefaultTitleMutationError = (
	error: Error | ApolloErrorInterop | null,
	contentId: string,
) => {
	setContentErrorAttributes(error, {
		objectId: contentId,
	});
	getMonitoringClient().submitError(error, {
		attribution: Attribution.PAGE_MODES,
	});
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EmojiWrapper: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.div({
	marginTop: token('space.025'),
	paddingRight: token('space.075'),
	minHeight: '40px',
});

const EmojiTitleButtonComponent: FC<EmojiTitleButtonComponentProps> = ({
	contentId,
	onUpdateStartEmoji,
	onUpdateEndEmoji,
	emojiTitleState,
	setSelectedEmoji,
	onEmojiPickerLoaded,
	isUnpublishedDraft,
	disableEmoji,
	floatingToolbarButtonStyle,
	pageTreeStateUpdater,
	contentType,
	isAutoPageEmojiEligible,
	emojiTitleDiscovered,
	isSmartCreate,
}) => {
	const [{ aiEmojiSuggestionSessionId }] = useAIEmojiSuggestion();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, userId } = useSessionData();
	const isLivePage = useIsCurrentPageLive();
	const emojiStatusFieldName = useEmojiStatusFieldName();
	const [defaulTitleEmojiMutationError, setDefaulTitleEmojiMutationError] = useState<
		Error | ApolloErrorInterop | null
	>(null);

	const setSelectedEmojiCallback = useCallback(
		(emoji: any) => {
			setSelectedEmoji && setSelectedEmoji(emoji);
		},
		[setSelectedEmoji],
	);

	const [savedEmoji, setSavedEmoji] = useState<
		OptionalEmojiDescription | Promise<OptionalEmojiDescription> | null
	>(null);

	const hasReplacedAIFCEmojiRef = useRef(false);
	useEffect(() => {
		if (aiEmojiSuggestionSessionId !== undefined) {
			hasReplacedAIFCEmojiRef.current = false;
		}
	}, [aiEmojiSuggestionSessionId]);

	const closeBothEmojiPicker = () => {
		void emojiTitleState.setOpenFirstEmojiPicker(false);
		void emojiTitleState.setOpenSecondEmojiPicker(false);
	};

	const updateIsRemovingEmoji = () => {
		void emojiTitleState.setIsRemovingEmoji(true);
	};

	const onClickInEditor = useCallback((event: any) => {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- only invoked client-side from a document click listener
		const emojiDialog = document.getElementById('emoji-picker');
		const EMOJI_POPUP_BUTTOM_ID = 'emoji-popup-button';

		const shouldCloseDialog = !(
			event.target?.classList?.contains('emoji') ||
			event.target?.classList?.contains('emoji-common-emoji-sprite') ||
			event.target?.classList?.contains('emoji-popup-button-text') ||
			event.target?.getAttribute('aria-label') === 'Add emoji' ||
			event.target?.parentNode?.classList?.contains('cover-picture') ||
			event.target?.getAttribute('aria-label') === 'Emoji' ||
			event.target?.classList?.contains('add-picture-button') ||
			event.target?.getAttribute('id') === EMOJI_POPUP_BUTTOM_ID ||
			event.target?.parentNode?.getAttribute('id') === EMOJI_POPUP_BUTTOM_ID ||
			event.target?.parentNode?.classList?.contains(EMOJI_POPUP_BUTTOM_ID) ||
			event.target?.parentNode?.classList?.contains('emoji-picker-add-emoji') ||
			event.target?.classList?.contains('add-picture-button') ||
			event.target?.getAttribute('aria-label') === 'image icon' ||
			// The following attributes come from EmojiTitleSelection and are used to ensure
			// the default icon's elements and its container are not registered as editor clicks
			event.target?.parentNode?.getAttribute('data-testid') === 'live-docs-default-title-icon' ||
			event.target?.getAttribute('data-testid') === 'live-docs-default-title-icon'
		);

		if (!emojiDialog?.contains(event.target) && shouldCloseDialog) {
			closeBothEmojiPicker();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// handle close emoji picker when user click outside the picker dialog
		document.addEventListener('click', onClickInEditor);

		return () => {
			document.removeEventListener('click', onClickInEditor);
			closeBothEmojiPicker();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		return () => {
			if (isUnpublishedDraft) {
				// track draft created with emoji
				void trackDraftCreatedWithEmoji({
					createAnalyticsEvent,
					contentId,
					isLivePage,
				});
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUnpublishedDraft]);

	const emojiTitleFragmentResult = useEmojiTitleFragment({ contentId });
	const emojiTitleQueryResult = useQueryInterop<EmojiTitleQuery$data, EmojiTitleQuery$variables>(
		EmojiTitleQueryArtifact,
		{
			variables: expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
				? {
						...createEditorContentUnifiedQueryVariables({
							contentId,
							draftShareId: getDraftShareIdFromQueryParam(),
						}),
						skipPublishedFragment: false,
					}
				: {
						contentId,
						status: ['current', 'draft'],
						draftShareId: getDraftShareIdFromQueryParam(),
						skipPublishedFragment: false,
					},
			skip: emojiTitleFragmentResult.isProvided,
		},
	);
	const { data, error } = emojiTitleFragmentResult.isProvided
		? emojiTitleFragmentResult
		: emojiTitleQueryResult;
	// The skipped query's refetch writes the records the fragment reads, so it updates the fragment.
	const { refetch: refetchEmoji } = emojiTitleQueryResult;

	const {
		updateContentProperty,
		updateContentPropertyResult: { loading: updateEmojiLoading } = {},
	} = useUpdateContentProperty({
		contentId,
		contentType: contentType || 'page',
		options: {
			refetchQueries: [
				{
					query: EmojiTitleQueryArtifact,
					variables: expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
						? {
								...createEditorContentUnifiedQueryVariables({
									contentId,
									draftShareId: getDraftShareIdFromQueryParam(),
								}),
								skipPublishedFragment: false,
							}
						: {
								contentId,
								status: ['current', 'draft'],
								draftShareId: getDraftShareIdFromQueryParam(),
								skipPublishedFragment: false,
							},
				},
			],
		},
	});

	const [updateDefaultTitleEmojiMutation, { loading: updateDefaultTitleEmojiLoading }] =
		useMutationInterop<
			EmojiTitleButtonUpdateDefaultTitleEmojiMutation$data,
			EmojiTitleButtonUpdateDefaultTitleEmojiMutation$variables
		>(EmojiTitleButtonUpdateDefaultTitleEmojiMutation, {
			refetchQueries: [
				{
					query: EmojiTitleQueryArtifact,
					variables: expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
						? {
								...createEditorContentUnifiedQueryVariables({
									contentId,
									draftShareId: getDraftShareIdFromQueryParam(),
								}),
								skipPublishedFragment: false,
							}
						: {
								contentId,
								status: ['current', 'draft'],
								draftShareId: getDraftShareIdFromQueryParam(),
								skipPublishedFragment: false,
							},
				},
			],
			awaitRefetchQueries: true,
		});

	const updateDefaultEmoji = useCallback(
		async ({ defaultTitleEmoji, onUpdateComplete }: UpdateDefaultEmojiArgs) => {
			if (!isLivePage) {
				return;
			}

			try {
				void emojiTitleState.setIsUpdatingEmoji(true);
				const { data: mutationData } = await updateDefaultTitleEmojiMutation({
					variables: {
						cloudId,
						input: {
							contentId,
							defaultTitleEmoji: defaultTitleEmoji as ConfluenceGraphQLDefaultTitleEmoji,
						},
					},
				});
				const result = (mutationData as any)?.confluence_updateDefaultTitleEmoji;
				if (result?.success) {
					void emojiTitleState.setHasLiveDocsDefaultEmoji(
						defaultTitleEmoji === 'LIVE_PAGE_DEFAULT',
					);
					setDefaulTitleEmojiMutationError(null);
					onUpdateComplete?.();
				}
				if (result?.errors?.length && result?.errors?.length > 0) {
					const transformedErrors = mutationErrorExtensionsAggCompat<
						MutationErrorExtensions,
						MutationErrorExtensionsAgg<MutationErrorExtensions>
					>(result.errors);
					transformedErrors.forEach((mutationError) => {
						if (
							(mutationError as any).message &&
							mutationError.extensions &&
							mutationError.extensions[0]?.statusCode
						) {
							const parsedError = new Error((mutationError as any).message || 'Unknown error');
							setDefaulTitleEmojiMutationError(parsedError);
							processDefaultTitleMutationError(parsedError, contentId);
						}
					});
				}
			} catch (gqlError) {
				setDefaulTitleEmojiMutationError(gqlError);
				processDefaultTitleMutationError(gqlError, contentId);
				markErrorAsHandled(gqlError);
			} finally {
				void emojiTitleState.setIsUpdatingEmoji(false);
			}
		},
		[isLivePage, contentId, cloudId, updateDefaultTitleEmojiMutation, emojiTitleState],
	);

	const refetchEmojiAndUpdatePageTreeSubscription = useCallback(async () => {
		await refetchEmoji();
		if (isLivePage) pageTreeStateUpdater.setForceFetchPageId(contentId);
	}, [refetchEmoji, isLivePage, pageTreeStateUpdater, contentId]);

	useSubscriptionEvent({
		contentId,
		contentType,
		delta: 'emojiTitleUpdated',
		subscriberId: 'emojiTitleButton',
		onDataReceived: refetchEmojiAndUpdatePageTreeSubscription,
	});

	useSubscriptionEvent({
		contentId,
		contentType,
		delta: 'emojiTitleDeleted',
		subscriberId: 'emojiTitleButtonDeleted',
		onDataReceived: refetchEmojiAndUpdatePageTreeSubscription,
	});

	const hasEmoji = !!(data && getHasEmoji(data, emojiStatusFieldName));
	const emojiId = (data && getEmojiId(data, emojiStatusFieldName)) ?? '';
	const hasDefaultTitleEmoji = isLivePage && getHasLiveDocsDefaultEmoji(data);

	const removeEmojiCallback = async (
		event: React.MouseEvent<Element | HTMLDivElement | HTMLTextAreaElement>,
	) => {
		setSavedEmoji(null);
		setSelectedEmojiCallback(null);
		closeBothEmojiPicker();
		updateIsRemovingEmoji();

		if (isLivePage) {
			// Default Emoji Removal
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'removeDefaultEmojiButton',
					actionSubjectId: 'removeDefaultEmojiButton_clicked',
					source: 'EmojiTitleButton',
					attributes: {
						contentId,
						isLivePage,
						hasLiveDocsDefaultEmoji: hasDefaultTitleEmoji,
					},
				},
			}).fire();

			void updateDefaultEmoji({ defaultTitleEmoji: DefaultTitleEmojiValues.NONE });
		}

		if (hasEmoji) {
			await onRemoveEmoji(event, {
				onUpdateStartEmoji,
				onUpdateEndEmoji,
				onEmojiUpdated: refetchEmoji,
				contentId,
				updateContentProperty,
				componentName: 'EmojiTitleButton',
				emojiStatusFieldName,
			});
		} else {
			// Handle clean up if not handled in onRemoveEmoji call
			onUpdateEndEmoji?.();
		}
	};

	// Restore the cached emoji via the normal set-emoji flow (not the
	// live-page-default sentinel). Silently bails so the caller falls back
	// to the Live Docs default.
	const restorePreviousEmoji = useCallback(async () => {
		const previousEmojiId = emojiTitleState.getPreviousEmojiId(contentId);
		if (
			!previousEmojiId ||
			previousEmojiId === DefaultTitleEmojiTransformerValues.LIVE_PAGE_DEFAULT ||
			!userId
		) {
			return;
		}

		// One provider for both lookup and onSelectEmoji (getEmojiFromEmojiId
		// would internally construct a second instance).
		const emojiProvider = await getEmojiProvider(cloudId, userId);
		if (!emojiProvider) {
			return;
		}

		const previousEmojiDescription = await emojiProvider.findById(previousEmojiId);
		if (!previousEmojiDescription) {
			return;
		}
		// `loadMediaEmoji` returns null for standard Unicode emojis — fall back to the description.
		const previousEmoji =
			(await emojiProvider.loadMediaEmoji(previousEmojiDescription)) ?? previousEmojiDescription;

		await onSelectEmoji(
			{
				selectedEmoji: previousEmoji,
				emojiProvider,
				onUpdateStartEmoji,
				createAnalyticsEvent,
				emojiTitleState,
				onEmojiUpdated: refetchEmoji,
				onUpdateEndEmoji,
				updateContentProperty,
				contentId,
				componentName: 'EmojiTitleButton',
				isLivePage,
				emojiStatusFieldName,
			},
			null,
		);
	}, [
		emojiTitleState,
		userId,
		cloudId,
		onUpdateStartEmoji,
		createAnalyticsEvent,
		refetchEmoji,
		onUpdateEndEmoji,
		updateContentProperty,
		contentId,
		isLivePage,
		emojiStatusFieldName,
	]);

	useEffect(() => {
		if (hasEmoji && emojiId.length > 0) {
			void getEmojiFromEmojiId({
				userId: userId!,
				cloudId,
				emojiId,
			}).then((emoji) => {
				emoji && setSavedEmoji(emoji);
				emoji && setSelectedEmojiCallback(emoji);
			});
		} else {
			setSelectedEmojiCallback(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasEmoji, emojiId]);

	useEffect(() => {
		if (
			isLivePage &&
			hasEmoji &&
			emojiId &&
			emojiId !== DefaultTitleEmojiTransformerValues.LIVE_PAGE_DEFAULT &&
			isExperimentEnabled('cc_page_experiences_livedocs_restore_latest_emoji')
		) {
			void emojiTitleState.setPreviousEmojiId(contentId, emojiId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLivePage, hasEmoji, emojiId, contentId]);

	return (
		<ErrorBoundary attribution={Attribution.PAGE_EXTENSIONS}>
			{error ? <ErrorDisplay error={error} /> : null}
			{defaulTitleEmojiMutationError ? (
				<ErrorDisplay error={defaulTitleEmojiMutationError} />
			) : null}
			<EmojiTitlePopover
				key="editor-emoji-title"
				emojiTitleState={emojiTitleState}
				contentId={contentId}
				isSmartCreate={isSmartCreate}
				currentEmojiId={emojiId}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
				isLoading={
					updateEmojiLoading ||
					emojiTitleState.state.isRemovingEmoji ||
					updateDefaultTitleEmojiLoading
				}
				setOpenSecondEmojiPicker={emojiTitleState.setOpenSecondEmojiPicker}
				openSecondEmojiPicker={emojiTitleState.state.openSecondEmojiPicker}
				setIsEmojiButtonFocused={emojiTitleState.setIsEmojiButtonFocused}
				userOffline={disableEmoji}
				emojiPicker={
					<EmojiPickerLoader
						contentId={contentId}
						onUpdateStartEmoji={onUpdateStartEmoji}
						emojiTitleState={emojiTitleState}
						onEmojiUpdated={refetchEmoji}
						onUpdateEndEmoji={onUpdateEndEmoji}
						updateContentProperty={updateContentProperty}
						closeBothEmojiPicker={closeBothEmojiPicker}
						componentName="EmojiTitleButton"
						isSmartCreate={isSmartCreate}
						hasReplacedAIFCEmojiRef={hasReplacedAIFCEmojiRef}
					/>
				}
				onEmojiPickerLoaded={onEmojiPickerLoaded}
				hasEmoji={hasEmoji && !!savedEmoji}
				onRemoveEmoji={removeEmojiCallback}
				updateDefaultEmoji={updateDefaultEmoji}
				hasLiveDocsDefaultEmoji={hasDefaultTitleEmoji}
				isAutoPageEmojiEligible={isAutoPageEmojiEligible}
				emojiTitleDiscovered={emojiTitleDiscovered}
				contentType={contentType ?? undefined}
				previousEmojiId={
					isExperimentEnabled('cc_page_experiences_livedocs_restore_latest_emoji')
						? emojiTitleState.getPreviousEmojiId(contentId)
						: null
				}
				restorePreviousEmoji={restorePreviousEmoji}
			/>
		</ErrorBoundary>
	);
};

const EmojiTitleButtonWithPTSU = withPageTreeStateUpdater(EmojiTitleButtonComponent);

export const EmojiTitleButton = (
	props: Omit<EmojiTitleButtonComponentProps, 'emojiTitleState' | 'pageTreeStateUpdater'>,
): React.JSX.Element => (
	<Subscribe to={[EmojiTitleStateContainer]}>
		{(emojiTitleState: EmojiTitleStateContainer) => (
			<EmojiTitleButtonWithPTSU
				{...props}
				emojiTitleState={emojiTitleState}
				data-testid="emoji-title-button"
			/>
		)}
	</Subscribe>
);
